import type { QueryOptions } from '@apollo/client';
import createGraphqlClient, {
  mocks as graphqlClientMockOverrides,
} from '@seek/ca-graphql-client';
import {
  browserHttpLink,
  nodeHttpLink,
} from '@seek/ca-graphql-client/src/http';
import { type Zone, brandAndCountryFromZone } from '@seek/audience-zones';

import { CHANGE_EMAIL } from './account/changeEmail.graphql';
import { CHANGE_PASSWORD } from './account/changePassword.graphql';
import { DELETE_ACCOUNT } from './account/deleteAccount.graphql';
import { GET_USER_QUERY } from './getUser.graphql';
import { handleAuthError, handleValidationErrors } from './errors';
import type { PasswordInput } from './types';

import notificationsMockOverrides from 'src/modules/graphql/notifications/notifications-mock-overrides';
import resolverOverrides from './resolver-overrides';
import { visibilityMockOverrides } from './visibility/visibility-mock-overrides';
import type {
  Brands,
  CountryCode,
} from '@seek/ca-graphql-client/src/http/getSeekHeaders';

export const createClient = (
  authOptions: any,
  appName: string,
  mock: boolean,
  zone: Zone,
  isServer?: boolean,
) => {
  const [brand, country] = brandAndCountryFromZone(zone) || ['SEEK', 'AU'];
  const graphqlClient = createGraphqlClient({
    brand: brand.toLowerCase() as Brands,
    country: country as CountryCode,
    headers: { Accept: 'application/features.seek.all+json' },
    mockOverrides: {
      ...graphqlClientMockOverrides,
      ...visibilityMockOverrides,
      ...notificationsMockOverrides,
    },
    resolverOverrides,
    appName,
    mock,
    httpLink: isServer ? nodeHttpLink : browserHttpLink,
    authOptions,
  });

  const mutateGql = async (options: {
    mutation: any;
    variables?: Record<string, unknown>;
  }) =>
    graphqlClient
      .mutate(options)
      .catch(handleAuthError)
      .catch(handleValidationErrors);

  const queryGql = (options: QueryOptions) =>
    graphqlClient.query({ fetchPolicy: 'no-cache', ...options });

  const changeEmail = ({
    newEmailAddress,
    password,
  }: {
    newEmailAddress: string;
    password: string;
  }) =>
    mutateGql({
      mutation: CHANGE_EMAIL,
      variables: {
        emailChange: {
          newEmailAddress,
          password,
        },
      },
    }).then((result) => result?.data?.changeEmailAddress);

  const changePassword = ({
    newPassword,
    password,
  }: {
    newPassword: string;
    password: string;
  }) =>
    mutateGql({
      mutation: CHANGE_PASSWORD,
      variables: {
        passwordChange: {
          newPassword,
          password,
        },
      },
    }).then((result) => result?.data?.changePassword);

  const deleteAccount = ({ password }: PasswordInput) =>
    mutateGql({
      mutation: DELETE_ACCOUNT,
      variables: {
        accountDelete: {
          password,
        },
      },
    }).then((result) => result?.data?.deleteAccount);

  const getUser = () =>
    queryGql({
      query: GET_USER_QUERY,
    });

  return {
    client: graphqlClient,
    changeEmail,
    changePassword,
    deleteAccount,
    getUser,
  };
};
